/**
 * Generate a tiny base64 JPEG thumbnail from a picked photo File.
 *
 * Android Telegram WebView (12.x on at least one Samsung device) has
 * been observed to reject every conventional decode path:
 *   - `<img src="blob:…">` → onError
 *   - `createImageBitmap(file)` → throws
 *   - `FileReader.readAsDataURL(file)` → onerror
 * …yet `fetch(formDataWithFile)` works. So the File's bytes are
 * readable through fetch's body serializer but not through the legacy
 * media APIs. We chain four fallbacks in the order most likely to
 * survive the WebView's restrictions, and the result helper returns a
 * trace of which paths were attempted and which succeeded so the bug
 * shows up in K8s `miniapp_client_error` logs.
 *
 * Trace format: comma-joined tags like
 *   "bitmap_attempt,bitmap_fail:cannot decode,filereader_attempt,filereader_fail:NotReadableError,arraybuffer_attempt,arraybuffer_ok,image_attempt,image_ok"
 *
 * Sizing target: 256px on the long edge at JPEG q=0.6 → ~5–10 KB.
 * Failure is non-fatal: callers fall back to no thumbnail.
 */
const MAX_DIMENSION = 256;
const QUALITY = 0.6;

export interface ThumbnailResult {
  url: string | null;
  trace: string[];
}

export async function generatePhotoThumbnail(
  file: File,
): Promise<ThumbnailResult> {
  const trace: string[] = [];
  if (!file.type.startsWith("image/")) {
    trace.push("non_image");
    return { url: null, trace };
  }
  // Animated GIFs and vector SVGs lose information through canvas raster
  // downscaling; let the slot show a kind-icon for those.
  if (file.type === "image/gif" || file.type === "image/svg+xml") {
    trace.push("skip_kind");
    return { url: null, trace };
  }

  // Path A — createImageBitmap accepts the File directly.
  if (typeof createImageBitmap === "function") {
    trace.push("bitmap_attempt");
    try {
      const bitmap = await createImageBitmap(file);
      try {
        const { width, height } = fitWithin(
          bitmap.width,
          bitmap.height,
          MAX_DIMENSION,
        );
        const dataUrl = drawToDataUrl(bitmap, width, height);
        if (dataUrl) {
          trace.push("bitmap_ok");
          return { url: dataUrl, trace };
        }
        trace.push("bitmap_canvas_empty");
      } finally {
        const close = (bitmap as ImageBitmap & { close?: () => void }).close;
        if (typeof close === "function") close.call(bitmap);
      }
    } catch (err) {
      trace.push(`bitmap_fail:${tagOf(err)}`);
    }
  } else {
    trace.push("bitmap_unavailable");
  }

  // Need a URL we can feed an <Image> for the fallback canvas decode.
  // Try in order: FileReader.readAsDataURL → file.arrayBuffer manual
  // base64.
  let dataUrl: string | null = null;

  trace.push("filereader_attempt");
  try {
    dataUrl = await readAsDataURL(file);
    trace.push(`filereader_ok:${String(dataUrl.length)}`);
  } catch (err) {
    trace.push(`filereader_fail:${tagOf(err)}`);
  }

  if (dataUrl === null) {
    trace.push("arraybuffer_attempt");
    try {
      const buffer = await file.arrayBuffer();
      dataUrl = bytesToDataUrl(new Uint8Array(buffer), file.type || "image/jpeg");
      trace.push(`arraybuffer_ok:${String(dataUrl.length)}`);
    } catch (err) {
      trace.push(`arraybuffer_fail:${tagOf(err)}`);
    }
  }

  if (dataUrl === null) {
    return { url: null, trace };
  }

  trace.push("image_attempt");
  try {
    const img = await loadImage(dataUrl);
    if (typeof img.decode === "function") {
      try {
        await img.decode();
      } catch {
        /* ignore — fall back to onload-fired state */
      }
    }
    const { width, height } = fitWithin(img.width, img.height, MAX_DIMENSION);
    const out = drawToDataUrl(img, width, height);
    if (out) {
      trace.push("image_ok");
      return { url: out, trace };
    }
    trace.push("image_canvas_empty");
  } catch (err) {
    trace.push(`image_fail:${tagOf(err)}`);
  }

  return { url: null, trace };
}

/**
 * Read a File's bytes as a `data:` URL. Tries `FileReader.readAsDataURL`
 * first; if it throws (some WebViews) falls back to `file.arrayBuffer`
 * + manual base64. Returns the chosen path in the trace.
 */
export interface DataUrlResult {
  url: string | null;
  trace: string[];
}

export async function fileToDataUrl(file: File): Promise<DataUrlResult> {
  const trace: string[] = [];

  trace.push("filereader_attempt");
  try {
    const url = await readAsDataURL(file);
    trace.push(`filereader_ok:${String(url.length)}`);
    return { url, trace };
  } catch (err) {
    trace.push(`filereader_fail:${tagOf(err)}`);
  }

  trace.push("arraybuffer_attempt");
  try {
    const buffer = await file.arrayBuffer();
    const url = bytesToDataUrl(
      new Uint8Array(buffer),
      file.type || "image/jpeg",
    );
    trace.push(`arraybuffer_ok:${String(url.length)}`);
    return { url, trace };
  } catch (err) {
    trace.push(`arraybuffer_fail:${tagOf(err)}`);
  }

  return { url: null, trace };
}

function drawToDataUrl(
  source: CanvasImageSource,
  width: number,
  height: number,
): string | null {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(source, 0, 0, width, height);
  try {
    return canvas.toDataURL("image/jpeg", QUALITY);
  } catch {
    return null;
  }
}

function readAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error("filereader_failed"));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Synchronous Uint8Array → base64 → data URL. Built in 32KB chunks so
 * we don't hit the spread-operator argument limit (~64K args on V8) on
 * multi-MB photos.
 */
function bytesToDataUrl(bytes: Uint8Array, mime: string): string {
  const CHUNK = 0x8000;
  const parts: string[] = [];
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const sub = bytes.subarray(i, Math.min(i + CHUNK, bytes.length));
    parts.push(String.fromCharCode.apply(null, Array.from(sub)));
  }
  return `data:${mime};base64,${btoa(parts.join(""))}`;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve(img);
    };
    img.onerror = () => {
      reject(new Error("image_decode_failed"));
    };
    img.src = src;
  });
}

function fitWithin(
  w: number,
  h: number,
  max: number,
): { width: number; height: number } {
  if (w <= max && h <= max) return { width: w, height: h };
  const ratio = w > h ? max / w : max / h;
  return {
    width: Math.max(1, Math.round(w * ratio)),
    height: Math.max(1, Math.round(h * ratio)),
  };
}

function tagOf(err: unknown): string {
  if (err instanceof Error) {
    const tag = err.name === "Error" ? err.message : `${err.name}:${err.message}`;
    return tag.slice(0, 60).replace(/[\n\r]/g, " ");
  }
  return String(err).slice(0, 60);
}
